import * as stylex from '@stylexjs/stylex';
import { TRACK_IMPACT, availableStages, stageIcons } from '@/impactReports/impactReport.types';
import { hasAnalyticsAdminCapability } from '@/services/authorization.service';
import { doTrack } from '@/track/track.service';
import { Icon, Slider } from '@seeqdev/qomponents';
import { colors } from '@seeqdev/qomponents/colors.stylex';

export interface ImpactReportSliderProps {
  setStageIdx: (stage: number) => void;
  stageIndex: number;
}

/**
 * This component renders the slider that determines how much of the Impact Report form is displayed.
 */
export const ImpactReportSlider = ({ setStageIdx, stageIndex }: ImpactReportSliderProps) => {
  const handleValueChange = (value: number[]) => {
    const nextStage = value[0];
    if (typeof nextStage !== 'number') return;
    setStageIdx(nextStage);
    doTrack(TRACK_IMPACT, 'Form Slider dragged', { stageIndex: nextStage });
  };
  const canReview = hasAnalyticsAdminCapability();
  const visualMaxStageIdx = availableStages.length - 1;
  const maxAccessibleStageIdx = canReview ? visualMaxStageIdx : availableStages.indexOf('quantify');

  const sliderValue = Math.min(stageIndex, visualMaxStageIdx);

  return (
    <div {...stylex.props(styles.root)}>
      <Slider
        xstyle={styles.slider}
        trackXstyle={styles.track}
        rangeXstyle={styles.range}
        thumbXstyle={styles.thumb}
        min={0}
        max={visualMaxStageIdx}
        maxAccessible={maxAccessibleStageIdx}
        step={1}
        value={sliderValue}
        onValueChange={handleValueChange}
      />

      {/* Display corresponding icon for the current step */}
      <div {...stylex.props(styles.legend)}>
        {stageIcons.map((icon, index) => (
          <div key={index}>
            <Icon icon={icon} type={index > stageIndex ? 'gray' : 'theme'} />
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = stylex.create({
  root: { display: 'flex', flexDirection: 'column', width: 370 },
  slider: { height: 20 },
  track: { backgroundColor: '#d9d9d9', borderRadius: 2, height: 3 },
  range: { borderRadius: 2, backgroundColor: colors.accentPressed },
  thumb: {
    'width': 10,
    'height': 20,
    'borderRadius': 3,
    'backgroundColor': { 'default': colors.accentPressed, ':hover': colors.link, ':active': colors.accentPressed },
    ':focus': { outlineStyle: 'none' },
  },
  legend: { display: 'flex', justifyContent: 'space-between', marginTop: 4 },
});
